import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { ChevronRightIcon } from "@heroicons/react/24/outline";

import { ILocationGroupAvailability } from "../../models/LocationGroup";
import classNames from "../../utils/classNames";

interface YardLocationCapacityCardProps {
  locationGroups: ILocationGroupAvailability[];
}

const capacityTextClass = (used: number, capacity: number): string => {
  if (capacity === 0) return "text-gray-500";
  const ratio = used / capacity;
  if (ratio >= 1) return "text-red-600";
  if (ratio >= 0.8) return "text-amber-600";
  return "text-gray-800";
};

const capacityBarClass = (used: number, capacity: number): string => {
  if (capacity === 0) return "bg-gray-300";
  const ratio = used / capacity;
  if (ratio >= 1) return "bg-red-500";
  if (ratio >= 0.8) return "bg-amber-500";
  return "bg-green-500";
};

const barWidth = (used: number, capacity: number): string =>
  capacity > 0 ? `${Math.min(100, (used / capacity) * 100)}%` : "0%";

export default function YardLocationCapacityCard({
  locationGroups,
}: YardLocationCapacityCardProps) {
  const { t } = useTranslation("production");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const totalCapacity = useMemo(
    () => locationGroups.reduce((sum, g) => sum + g.capacity, 0),
    [locationGroups]
  );
  const totalUsed = useMemo(
    () => locationGroups.reduce((sum, g) => sum + g.vehicleCount, 0),
    [locationGroups]
  );

  const toggle = (locationGroupId: string) =>
    setExpandedId((prev) => (prev === locationGroupId ? null : locationGroupId));

  return (
    <div className="bg-white border border-gray-200 rounded-md px-3 py-2 flex flex-col h-full min-h-0">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-wide text-gray-500">
          {t("Yard Location Capacity")}
        </span>
        <span
          className={classNames(
            "text-sm font-semibold",
            capacityTextClass(totalUsed, totalCapacity)
          )}
        >
          {totalUsed} / {totalCapacity}
        </span>
      </div>
      <div className="mt-1 flex-1 min-h-0 overflow-y-auto space-y-1 pr-1 max-h-64 lg:max-h-none">
        {locationGroups.length === 0 ? (
          <div className="text-xs text-gray-400">{t("No location groups")}</div>
        ) : (
          locationGroups.map((g) => {
            const isExpanded = expandedId === g.locationGroupId;
            return (
              <div key={g.locationGroupId}>
                <button
                  type="button"
                  onClick={() => toggle(g.locationGroupId)}
                  className="w-full flex items-center gap-2 text-xs rounded px-1 py-0.5 hover:bg-gray-50"
                >
                  <ChevronRightIcon
                    className={classNames(
                      "h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform",
                      isExpanded && "rotate-90"
                    )}
                  />
                  <span className="flex-1 truncate text-left text-gray-700">{g.name}</span>
                  <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={classNames("h-full", capacityBarClass(g.vehicleCount, g.capacity))}
                      style={{ width: barWidth(g.vehicleCount, g.capacity) }}
                    />
                  </div>
                  <span
                    className={classNames(
                      "tabular-nums text-[11px] w-12 text-right",
                      capacityTextClass(g.vehicleCount, g.capacity)
                    )}
                  >
                    {g.vehicleCount} / {g.capacity}
                  </span>
                </button>

                {isExpanded && (
                  <div className="ml-5 mt-0.5 mb-1 space-y-1 border-l border-gray-100 pl-2">
                    {g.locations.length === 0 ? (
                      <div className="text-[11px] text-gray-400">{t("No locations in this group")}</div>
                    ) : (
                      g.locations.map((loc) => (
                        <div key={loc.id} className="flex items-center gap-2 text-xs">
                          <span className="flex-1 truncate text-gray-600">{loc.name}</span>
                          <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={classNames(
                                "h-full",
                                capacityBarClass(loc.vehicleCount, loc.capacity)
                              )}
                              style={{ width: barWidth(loc.vehicleCount, loc.capacity) }}
                            />
                          </div>
                          <span
                            className={classNames(
                              "tabular-nums text-[11px] w-12 text-right",
                              capacityTextClass(loc.vehicleCount, loc.capacity)
                            )}
                          >
                            {loc.vehicleCount} / {loc.capacity}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
